/**
 * Auth web API contracts.
 *
 * Defines the request/response schemas for cookie-based web authentication.
 * These endpoints are provided by Better Auth at /api/auth/*.
 */

import { oc } from "@orpc/contract";
import { z } from "zod";

export const SessionSchema = z.object({
  id: z.string(),
  userId: z.string(),
  expiresAt: z.string(),
  token: z.string(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Session = z.infer<typeof SessionSchema>;

export const WebUserSchema = z.object({
  id: z.string(),
  email: z.string().nullable(),
  name: z.string().nullable(),
  emailVerified: z.boolean(),
  image: z.string().nullable(),
  role: z.string(), // Non-nullable - DB has NOT NULL with default "user"
  banned: z.boolean().nullable(),
  banReason: z.string().nullable(),
  banExpires: z.string().nullable(),
  isAnonymous: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type WebUser = z.infer<typeof WebUserSchema>;

export const SessionDataSchema = z.object({
  session: SessionSchema,
  user: WebUserSchema,
});
export type SessionData = z.infer<typeof SessionDataSchema>;

export const WebAuthErrorSchema = z.object({
  message: z.string().optional(),
  code: z.string().optional(),
});
export type WebAuthError = z.infer<typeof WebAuthErrorSchema>;

export const SignInRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});
export type SignInRequest = z.infer<typeof SignInRequestSchema>;

export const SignUpRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
  name: z.string().min(1),
});
export type SignUpRequest = z.infer<typeof SignUpRequestSchema>;

export const ForgotPasswordRequestSchema = z.object({
  email: z.string().email(),
});
export type ForgotPasswordRequest = z.infer<typeof ForgotPasswordRequestSchema>;

export const ResetPasswordRequestSchema = z.object({
  token: z.string().min(1),
  newPassword: z.string().min(8),
});
export type ResetPasswordRequest = z.infer<typeof ResetPasswordRequestSchema>;

export const SuccessResponseSchema = z.object({
  success: z.boolean(),
});
export type SuccessResponse = z.infer<typeof SuccessResponseSchema>;

/**
 * Auth web contract - Cookie-based auth endpoints for web apps.
 *
 * These endpoints are served by Better Auth at /api/auth/*.
 * Note: Uses OpenAPI paths that match Better Auth's route structure.
 */
export const authContract = {
  /**
   * Get current session. Returns null if not authenticated.
   */
  getSession: oc
    .route({ method: "GET", path: "/get-session" })
    .output(SessionDataSchema.nullable()),

  /**
   * Sign in with email and password.
   */
  signIn: oc
    .route({ method: "POST", path: "/sign-in/email" })
    .input(SignInRequestSchema)
    .output(SessionDataSchema),

  /**
   * Sign up with email and password.
   */
  signUp: oc
    .route({ method: "POST", path: "/sign-up/email" })
    .input(SignUpRequestSchema)
    .output(SessionDataSchema),

  /**
   * Sign out the current session.
   */
  signOut: oc
    .route({ method: "POST", path: "/sign-out" })
    .output(SuccessResponseSchema),

  /**
   * Sign in anonymously (create anonymous session).
   */
  signInAnonymous: oc
    .route({ method: "POST", path: "/sign-in/anonymous" })
    .output(SessionDataSchema),

  /**
   * Request password reset email.
   */
  forgotPassword: oc
    .route({ method: "POST", path: "/forgot-password" })
    .input(ForgotPasswordRequestSchema)
    .output(SuccessResponseSchema),

  /**
   * Reset password with token.
   */
  resetPassword: oc
    .route({ method: "POST", path: "/reset-password" })
    .input(ResetPasswordRequestSchema)
    .output(SuccessResponseSchema),
};
